import { useCallback, useEffect, useRef } from 'react'

interface SectionContentProps<T> {
  targetElement: T | null
}

export default function useDragElement<T extends HTMLElement>({
  targetElement,
}: SectionContentProps<T>) {
  const isDown = useRef<boolean>(false)
  const posX = useRef<number>(0)

  const handleMouseDown = useCallback(
    (e) => {
      if (targetElement) {
        isDown.current = true
        posX.current = e.clientX
      }
    },
    [targetElement],
  )

  const handleMouseLeave = useCallback(() => {
    isDown.current = false
  }, [])

  const handleMouseMove = useCallback(
    (e) => {
      if (isDown.current && targetElement) {
        targetElement.scrollLeft -= (e.clientX - posX.current) / 10
      }
    },
    [targetElement],
  )

  const handleTouchStart = useCallback((e: TouchEvent) => {
    e.stopPropagation()
  }, [])

  const handleTouchEnd = useCallback((e: TouchEvent) => {
    e.stopPropagation()
  }, [])

  const handleCleanUp = useCallback(() => {
    if (!targetElement) return
    targetElement.removeEventListener('touchstart', handleTouchStart)
    targetElement.removeEventListener('touchend', handleTouchEnd)
  }, [handleTouchEnd, handleTouchStart, targetElement])

  useEffect(() => {
    if (targetElement) {
      targetElement.addEventListener('mouseleave', handleMouseLeave)
      targetElement.addEventListener('mouseup', handleMouseLeave)
      targetElement.addEventListener('mousedown', handleMouseDown)
      targetElement.addEventListener('mousemove', handleMouseMove)
      targetElement.addEventListener('touchstart', handleTouchStart)
      targetElement.addEventListener('touchend', handleTouchEnd)
    }
    return () => handleCleanUp()
  }, [
    handleMouseMove,
    handleMouseDown,
    handleMouseLeave,
    handleTouchStart,
    handleTouchEnd,
    handleCleanUp,
    targetElement,
  ])
}
